import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Loader2, Mail, MapPin } from "lucide-react";
import { PageIntro } from "../components/PageIntro";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitContact, submitWaitlist } from "../lib/api";
import { waLink, WHATSAPP_DISPLAY } from "../lib/data";

const fieldCls =
  "mt-2 bg-transparent border-0 border-b border-black/15 rounded-none px-0 text-brand-ink placeholder:text-brand-muted/40 focus-visible:ring-0 focus-visible:border-brand-sprout";
const labelCls = "text-brand-muted font-mono text-xs uppercase tracking-wider";

export default function Contact() {
  const [c, setC] = useState({ name: "", email: "", phone: "", message: "" });
  const [cLoading, setCLoading] = useState(false);
  const [cDone, setCDone] = useState(false);

  const [w, setW] = useState({ name: "", pincode: "", phone: "", email: "" });
  const [wLoading, setWLoading] = useState(false);
  const [wDone, setWDone] = useState(false);

  const uc = (k) => (e) => setC((s) => ({ ...s, [k]: e.target.value }));
  const uw = (k) => (e) => setW((s) => ({ ...s, [k]: e.target.value }));

  const onContact = async (e) => {
    e.preventDefault();
    if (!c.name || !c.email || !c.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setCLoading(true);
    try {
      await submitContact(c);
      setCDone(true);
      setC({ name: "", email: "", phone: "", message: "" });
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setCLoading(false);
    }
  };

  const onWaitlist = async (e) => {
    e.preventDefault();
    if (!w.name || !w.pincode || !w.phone) {
      toast.error("Please fill in your name, pincode and phone.");
      return;
    }
    setWLoading(true);
    try {
      await submitWaitlist({ ...w, email: w.email || null });
      setWDone(true);
      setW({ name: "", pincode: "", phone: "", email: "" });
      toast.success("You're on the waitlist! We'll notify you when a unit opens.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setWLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageIntro
        label="Contact us"
        titleLines={["Everything starts", "with a message."]}
        subtitle="The fastest way to reach 40X Leaves is WhatsApp — subscriptions, support, and franchise enquiries all start there."
      />

      <section className="px-5 md:px-10 py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left info */}
          <div className="lg:col-span-4 space-y-8">
            <Reveal>
              <a
                href={waLink("Hi 40X Leaves, my pincode is ")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="block bg-brand-forest text-brand-bg rounded-[1.5rem] p-8 transition-transform duration-300 hover:-translate-y-1"
              >
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-bg/70">Customers</p>
                <p className="mt-3 font-serif text-2xl">Send your pincode to get started</p>
                <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold">
                  {WHATSAPP_DISPLAY} <ArrowUpRight size={16} />
                </p>
              </a>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="bg-brand-card rounded-[1.5rem] p-8 border border-black/5 space-y-4">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-sprout">Company</p>
                <p className="font-serif text-xl text-brand-ink">40X Leaves Microgreens Private Limited</p>
                <div className="space-y-2 text-sm text-brand-muted">
                  <p className="flex items-center gap-2"><Mail size={15} /> hello@40xleaves.in</p>
                  <p className="flex items-center gap-2"><Mail size={15} /> franchise@40xleaves.in</p>
                  <p className="flex items-center gap-2"><MapPin size={15} /> Kerala, India</p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="bg-brand-bg rounded-[1.5rem] p-8 md:p-10 border border-black/10">
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
                  Send a message
                </p>
                {cDone ? (
                  <div data-testid="contact-success" className="flex flex-col items-start gap-4 py-6">
                    <div className="w-14 h-14 rounded-full bg-brand-sprout flex items-center justify-center">
                      <Check size={26} className="text-white" />
                    </div>
                    <h3 className="font-serif text-3xl text-brand-ink">Message sent.</h3>
                    <p className="text-brand-muted max-w-md">Thanks for reaching out — we'll get back to you soon.</p>
                    <button onClick={() => setCDone(false)} className="text-sm font-semibold text-brand-forest hover:underline">Send another</button>
                  </div>
                ) : (
                  <form onSubmit={onContact} data-testid="contact-form" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="c-name" className={labelCls}>Full name *</Label>
                        <Input id="c-name" data-testid="contact-name" value={c.name} onChange={uc("name")} className={fieldCls} placeholder="Your name" />
                      </div>
                      <div>
                        <Label htmlFor="c-phone" className={labelCls}>Phone</Label>
                        <Input id="c-phone" data-testid="contact-phone" value={c.phone} onChange={uc("phone")} className={fieldCls} placeholder="Your phone" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="c-email" className={labelCls}>Email *</Label>
                      <Input id="c-email" type="email" data-testid="contact-email" value={c.email} onChange={uc("email")} className={fieldCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <Label htmlFor="c-message" className={labelCls}>Message *</Label>
                      <Textarea id="c-message" data-testid="contact-message" rows={4} value={c.message} onChange={uc("message")} className={`${fieldCls} resize-none`} placeholder="How can we help?" />
                    </div>
                    <button type="submit" data-testid="contact-submit" disabled={cLoading} className="inline-flex items-center gap-2 rounded-full bg-brand-forest text-brand-bg px-8 py-4 text-sm font-semibold transition-transform duration-300 hover:scale-105 hover:bg-brand-sprout disabled:opacity-60">
                      {cLoading ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />}
                      {cLoading ? "Sending…" : "Send message"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section className="px-5 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="max-w-[1400px] mx-auto rounded-[2rem] bg-brand-dark text-brand-bg p-8 md:p-16 relative overflow-hidden">
            <div className="noise-overlay" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-5">
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">No grower yet?</p>
                <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">Join the waitlist for your pincode.</h2>
                <p className="mt-6 text-brand-bg/70 leading-relaxed">Be the first to know when a 40X Leaves unit opens near you. High-demand pincodes get new units fastest.</p>
              </div>
              <div className="md:col-span-6 md:col-start-7">
                {wDone ? (
                  <div data-testid="waitlist-success" className="flex flex-col items-start gap-4 h-full justify-center">
                    <div className="w-14 h-14 rounded-full bg-brand-sprout flex items-center justify-center">
                      <Check size={26} className="text-white" />
                    </div>
                    <h3 className="font-serif text-3xl">You're on the list.</h3>
                    <p className="text-brand-bg/70 max-w-md">We'll notify you the moment a unit opens in your area.</p>
                    <button onClick={() => setWDone(false)} className="text-sm font-semibold text-brand-sprout hover:underline">Add another pincode</button>
                  </div>
                ) : (
                  <form onSubmit={onWaitlist} data-testid="waitlist-form" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="w-name" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Name *</Label>
                        <Input id="w-name" data-testid="waitlist-name" value={w.name} onChange={uw("name")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="Your name" />
                      </div>
                      <div>
                        <Label htmlFor="w-pincode" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Pincode *</Label>
                        <Input id="w-pincode" data-testid="waitlist-pincode" value={w.pincode} onChange={uw("pincode")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="e.g. 682001" />
                      </div>
                      <div>
                        <Label htmlFor="w-phone" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Phone *</Label>
                        <Input id="w-phone" data-testid="waitlist-phone" value={w.phone} onChange={uw("phone")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="Your phone" />
                      </div>
                      <div>
                        <Label htmlFor="w-email" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Email</Label>
                        <Input id="w-email" type="email" data-testid="waitlist-email" value={w.email} onChange={uw("email")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="you@email.com" />
                      </div>
                    </div>
                    <button type="submit" data-testid="waitlist-submit" disabled={wLoading} className="inline-flex items-center gap-2 rounded-full bg-brand-sprout text-white px-8 py-4 text-sm font-semibold transition-transform duration-300 hover:scale-105 disabled:opacity-60">
                      {wLoading ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />}
                      {wLoading ? "Joining…" : "Join the waitlist"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
